import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Alert,
  Box,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  uploadNewsletterBannerImage,
  buildNewsletterBannerImageUrl,
} from "../../api/newsletterBanner";

export type NewsletterBannerForm = {
  id: string;
  image?: string | null;
  updatedAt?: string | null;
};

type Props = {
  open: boolean;
  banner: NewsletterBannerForm | null;
  onClose: () => void;
  onSave: (imagePath: string) => Promise<void> | void;
};

const NewsletterBannerFormDialog: React.FC<Props> = ({
  open,
  banner,
  onClose,
  onSave,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const existingUrl = useMemo(() => {
    const path = banner?.image || "";
    return path ? buildNewsletterBannerImageUrl(path) : "";
  }, [banner?.image]);

  const previewUrl = useMemo(() => {
    if (!file) return "";
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (open) {
      setFile(null);
      setLocalError(null);
      setSaving(false);
    }
  }, [open, banner?.id]);

  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setLocalError(null);

    if (!f) return;

    if (!f.type.startsWith("image/")) {
      setLocalError("Please select an image file.");
      e.target.value = "";
      return;
    }

    setFile(f);
    e.target.value = "";
  };

  const clearSelected = () => {
    setFile(null);
    setLocalError(null);
  };

  const handleSave = async () => {
    setLocalError(null);

    const bannerId = banner?.id || "";
    const hasExistingImage = !!banner?.image;

    if (!bannerId && !file) {
      setLocalError("Please choose an image to upload.");
      return;
    }

    if (bannerId && !file && !hasExistingImage) {
      setLocalError("Please choose an image to upload.");
      return;
    }

    setSaving(true);
    try {
      let finalPath = banner?.image || "";
      if (file) {
        finalPath = await uploadNewsletterBannerImage(file);
      }

      await onSave(finalPath);

      setFile(null);
      onClose();
    } catch (e: any) {
      setLocalError(e?.message || "Failed to save banner.");
    } finally {
      setSaving(false);
    }
  };

  if (!banner) return null;

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>
        {banner.id ? "Edit Newsletter Banner" : "Upload Newsletter Banner"}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} mt={1}>
          {localError && <Alert severity="error">{localError}</Alert>}

          <Typography variant="body2" color="text.secondary">
            Upload banner image from your device (computer/mobile). No URL, no
            text.
          </Typography>

          <Box
            sx={{
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "#fafafa",
              height: 220,
              display: "grid",
              placeItems: "center",
            }}
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Selected banner preview"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : existingUrl ? (
              <img
                src={existingUrl}
                alt="Current banner"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Typography color="text.secondary">
                No banner uploaded yet.
              </Typography>
            )}
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<AddPhotoAlternateIcon />}
              disabled={saving}
            >
              Choose Image
              <input type="file" hidden accept="image/*" onChange={pickFile} />
            </Button>

            <Button
              variant="text"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={clearSelected}
              disabled={!file || saving}
            >
              Remove Selected
            </Button>
          </Stack>

          {file && (
            <Typography variant="caption" color="text.secondary">
              Selected: {file.name}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Uploading..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewsletterBannerFormDialog;
